// src/pages/MemberTransactionsPage.jsx

import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    CircularProgress,
    Chip
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import './index.css';
import { getTransactions } from 'apis/controllers/membership';
import ExplorerCard from 'ui-component/cards/ExplorerCard';

const statusColors = {
    Paid: 'success',
    Pending: 'warning',
    Failed: 'error'
};

const getCurrency = (cur) => {
    if (cur === 'CAD') return '$';
    else return '€';
};

const MemberTransactions = () => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchTransactions = async () => {
        try {
            const res = await getTransactions(); // replace with real endpoint
            setTransactions(res?.data || []);
        } catch (e) {
            enqueueSnackbar(t('Failed to load transactions'), { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTransactions();
    }, []);

    return (
        <ExplorerCard>
            <Typography variant="h3" fontWeight="bold" gutterBottom>
                {t('My Transactions')}
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} elevation={3} className="transactions-table-container">
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell className="sticky-header">Date</TableCell>
                                <TableCell className="sticky-header">Member Name</TableCell>
                                <TableCell className="sticky-header">Membership Type</TableCell>
                                {/* <TableCell className="sticky-header">Discount Code</TableCell> */}
                                {/* <TableCell className="sticky-header">Saved Date</TableCell> */}
                                <TableCell className="sticky-header">Payment Date</TableCell>
                                <TableCell className="sticky-header" align="right">
                                    Amount
                                </TableCell>
                                <TableCell className="sticky-header" align="center">
                                    Status
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {transactions.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        {t('No transactions found')}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                transactions.map((txn, idx) => (
                                    <TableRow key={idx} className="striped-row">
                                        <TableCell>{format(new Date(txn.date), 'yyyy-MM-dd HH:mm')}</TableCell>
                                        <TableCell>{txn.fullName}</TableCell>
                                        <TableCell>{txn?.discountCode ? `${txn.product} (${txn?.discountCode})` : txn?.product}</TableCell>
                                        {/* <TableCell>{txn.discountCode || '-'}</TableCell> */}
                                        {/* <TableCell>{txn.createdAt ? format(new Date(txn.createdAt), 'yyyy-MM-dd HH:mm') : '-'}</TableCell> */}
                                        <TableCell>
                                            {txn.paymentDate ? format(new Date(txn.paymentDate), 'yyyy-MM-dd HH:mm') : '-'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <strong>
                                                {`${getCurrency(txn?.currency)}${txn.amount.toFixed(2)} 
                                            ${txn.currency}`}
                                            </strong>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={txn.status}
                                                className="status-chip"
                                                icon={
                                                    ['Paid', 'Submitted']?.includes(txn.status) ? (
                                                        <span style={{ color: 'green' }}>✔️</span>
                                                    ) : ['Pending', 'Recheck', 'Payment Pending']?.includes(txn.status) ? (
                                                        <span style={{ color: '#ed6c02' }}>⏳</span>
                                                    ) : (
                                                        <span style={{ color: '#d32f2f' }}>❌</span>
                                                    )
                                                }
                                                sx={{
                                                    backgroundColor: ['Paid', 'Submitted']?.includes(txn.status)
                                                        ? '#d2f6e9'
                                                        : ['Pending', 'Recheck', 'Payment Pending']?.includes(txn.status)
                                                        ? '#fff4e5'
                                                        : '#fdecea',
                                                    color: ['Paid', 'Submitted']?.includes(txn.status)
                                                        ? '#2e7d32'
                                                        : ['Pending', 'Recheck', 'Payment Pending']?.includes(txn.status)
                                                        ? '#ed6c02'
                                                        : '#d32f2f'
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </ExplorerCard>
    );
};

export default MemberTransactions;
